import * as React from "react";
import router5CreateRouter, { Router } from "router5";
import browserPlugin from "router5-plugin-browser";
import { reduxPlugin } from "redux-router5";
import { shouldUpdateNode } from "router5-transition-path";
import { initRoutes } from "./apps";
import { connect } from "react-redux";
import { RouterProvider } from "react-router5";

export class Route {
  name: string;
  path: string;
  comp?: React.Component<any, any>;
  nested?: boolean;
}

class RelayRouter {
  private routeMap = {};
  private router;

  private static instance: RelayRouter;

  constructor() {
    this.routeMap = {};

    const routes = initRoutes();

    // Create router
    this.router = router5CreateRouter(routes, {
      defaultRoute: "home",
    });

    this.router.usePlugin(browserPlugin());

    // Populate the route map for reverse lookup and resolve components
    this.mapRoutes("", routes);
    this.resolveRouteComponents();
  }

  private mapRoutes(baseName: string, routes) {
    for (let r of routes) {
      const name = baseName ? `${baseName}.${r.name}` : r.name;

      this.routeMap[name] = r;

      if (r.children) {
        this.mapRoutes(name, r.children);
      }
    }
  }

  private resolveRouteComponents() {
    for (let name in this.routeMap) {
      const route = this.routeMap[name];

      if (route.nested && route.comp) {
        throw new Error(`Invalid route ${name} - nested routes should not have a component`);
      }

      if (!route.comp && !route.forwardTo) {
        const nameParts = name.split(".");
        if (nameParts.length == 1) {
          throw new Error(`Invalid route ${name} - does not have a component and a parent.`);
        }

        for (let i = nameParts.length - 1; i >= 0; i--) {
          const parentName = nameParts.slice(0, i).join(".");
          const parentRoute = this.routeMap[parentName];

          if (parentRoute && parentRoute.comp) {
            route.comp = parentRoute.comp;
            break;
          }
        }

        if (!route.comp) {
          throw new Error(`Invalid route ${name} - unable to resolve component.`);
        }
      }
    }
  }

  connectStore(store) {
    this.router.usePlugin(reduxPlugin(store.dispatch));
    this.router.setDependency("store", store);
  }

  start(callback) {
    this.router.start(callback);
  }

  // Public APIs
  getRoute(name: string) {
    return this.routeMap[name];
  }

  navigate(routeName: string, routeParams = {}, options = {}) {
    return this.router.navigate(routeName, routeParams, options);
  }

  buildUrl(routeName: string, routeParams = {}) {
    return this.router.buildUrl(routeName, routeParams);
  }

  // Singleton methods
  static create() {
    if (RelayRouter.instance) throw new Error("Relay router is already initialized");

    RelayRouter.instance = new RelayRouter();
    return RelayRouter.instance;
  }

  static getInstance() {
    if (!RelayRouter.instance) throw new Error("Relay router is not initialized");

    return RelayRouter.instance;
  }

  getRouter() {
    return this.router;
  }
}

// Helper APIs
export function createRouter() {
  return RelayRouter.create();
}

export function navigate(routeName: string, routeParams?: any, options?: any) {
  return RelayRouter.getInstance().navigate(routeName, routeParams, options);
}

export function buildUrl(routeName, routeParams, absolute = false) {
  let url = RelayRouter.getInstance().buildUrl(routeName, routeParams);

  if (absolute) {
    url = window.location.protocol + "//" + window.location.host + url;
  }

  return url;
}

export function getRouterStore(router: Router) {
  const deps = router.getDependencies();
  return deps.store || null;
}

// Root router component
const RouterComponent = ({ route }) => {
  const r = RelayRouter.getInstance().getRoute(route.name);
  const router = RelayRouter.getInstance().getRouter();

  return <RouterProvider router={router}>{r.comp(route.params)}</RouterProvider>;
};

// TODO: Potentially figure out if we want to use explicit partial updates or not
function createRootNodeSelector(routeNode: string, reducerKey = "router") {
  const routerStateSelector = (state) => state[reducerKey] || (state.get && state.get(reducerKey));

  let lastReturnedValue;

  return function (state) {
    const { route, previousRoute } = routerStateSelector(state);

    let shouldUpdate = true;
    if (route) {
      const rule = RelayRouter.getInstance().getRoute(route.name);
      if (rule.nested) {
        shouldUpdate = shouldUpdateNode(routeNode)(route, previousRoute);
      }
    } else {
      shouldUpdate = true;
    }

    if (!lastReturnedValue) {
      lastReturnedValue = { route, previousRoute };
    } else if (!previousRoute || (previousRoute !== route && shouldUpdate)) {
      lastReturnedValue = { route, previousRoute };
    }

    return lastReturnedValue;
  };
}

export const RootComponent = connect(createRootNodeSelector(""))(RouterComponent);
